<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useSessionStore } from "@/stores/session";
import { api } from "@/api";
import { canAccessConsoleRoute } from "@/access-control";

const route = useRoute();
const router = useRouter();
const session = useSessionStore();
onMounted(session.restore);
const showShell = computed(() => route.name !== "login");
const permissionList = computed(() => session.profile?.permissions ?? []);
const canOpen = (path: string) =>
  canAccessConsoleRoute(path, session.domain, permissionList.value);
const navigation = computed(() => [
  { to: "/", label: "总览", icon: "▦", show: canOpen("/") },
  {
    to: "/admin-accounts",
    label: "后台账号与权限",
    icon: "权",
    show: canOpen("/admin-accounts"),
  },
  {
    to: "/users",
    label: "用户中心管理",
    icon: "人",
    show: canOpen("/users"),
  },
  {
    to: "/notifications",
    label: "通知中心",
    icon: "信",
    show: canOpen("/notifications"),
  },
  {
    to: "/matches",
    label: "赛事中心",
    icon: "赛",
    show: canOpen("/matches"),
  },
  {
    to: "/operations",
    label: session.domain === "creator" ? "我的直播" : "直播中心",
    icon: "播",
    show: canOpen("/operations"),
  },
  {
    to: "/chat-moderation",
    label: "聊天审核",
    icon: "审",
    show: canOpen("/chat-moderation"),
  },
  {
    to: "/data-center",
    label: "数据中心",
    icon: "数",
    show: canOpen("/data-center"),
  },
  {
    to: "/system-settings",
    label: "系统设置",
    icon: "设",
    show: canOpen("/system-settings"),
  },
  {
    to: "/payment-settings",
    label: "资金控制",
    icon: "¥",
    show: canOpen("/payment-settings"),
  },
  {
    to: "/app-settings",
    label: "APP 管理",
    icon: "▣",
    show: canOpen("/app-settings"),
  },
  { to: "/kyc", label: "实名审核", icon: "✓", show: canOpen("/kyc") },
  {
    to: "/withdrawals",
    label: "提现复核",
    icon: "¥",
    show: canOpen("/withdrawals"),
  },
  {
    to: "/approvals",
    label: "双人审批",
    icon: "⇄",
    show: canOpen("/approvals"),
  },
  {
    to: "/audit",
    label: "审计日志",
    icon: "⌁",
    show: canOpen("/audit"),
  },
  {
    to: "/release-readiness",
    label: "发布准入",
    icon: "闸",
    show: canOpen("/release-readiness"),
  },
]);

async function logout() {
  try {
    await api.admin.logoutAdmin({ allSessions: false });
  } finally {
    session.clear();
    await router.replace("/login");
  }
}
</script>

<template>
  <div
    v-if="showShell"
    class="console-shell"
    :style="{ '--accent': session.currentDomain.accent }"
  >
    <aside class="sidebar">
      <div class="console-brand">
        <span>178</span>
        <div>
          <strong>直播</strong><small>{{ session.currentDomain.label }}</small>
        </div>
      </div>
      <nav>
        <RouterLink
          v-for="item in navigation.filter((entry) => entry.show)"
          :key="item.to"
          :to="item.to"
        >
          <i>{{ item.icon }}</i
          ><span>{{ item.label }}</span>
        </RouterLink>
      </nav>
      <div class="security-note">
        <i>盾</i>
        <div>
          <strong>安全会话</strong><small>MFA · IP 白名单 · 全量审计</small>
        </div>
      </div>
      <button class="logout" @click="logout">退出当前账号</button>
    </aside>
    <div class="console-main">
      <header class="topbar">
        <div>
          <span class="environment">PROTECTED CONSOLE</span
          ><strong>{{ session.currentDomain.host }}</strong>
        </div>
        <div class="operator">
          <span>{{ session.profile?.displayName?.slice(0, 1) }}</span>
          <div>
            <strong>{{ session.profile?.displayName }}</strong
            ><small>{{ session.profile?.username }}</small>
          </div>
        </div>
      </header>
      <p
        v-if="route.query.denied === '1'"
        class="console-access-denied"
        role="alert"
      >
        当前管理域或账号权限不能访问该功能，已返回总览。
      </p>
      <main class="workspace"><RouterView /></main>
    </div>
  </div>
  <RouterView v-else />
</template>
